import { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { BiSolidError } from "react-icons/bi";
import { FaPlus } from "react-icons/fa";
import { IoCloseSharp, IoEye } from "react-icons/io5";
import "react-loading-skeleton/dist/skeleton.css";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useDebounce } from "use-debounce";

import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import ActionMenu from "../../../component/ActionMenu";
import DriverInfo from "../../../component/Model/DriverInfo";
import AdminPagination from "../../../component/pagination/AdminPagination";
import SkeletonLoading from "../../../component/SkeletonLoading";
import Tooltip from "../../../component/Tooltip";
import i18n from "../../../i8next";
import {
  useDeleteDriverMutation,
  useGetDriversQuery,
} from "../../../store/api/vendor/driversSlice";
import { getErrorMessage } from "../../../utils";

const initialActions = {
  search: "",
};

const initialPagination = {
  limit: 10,
  page: 1,
  total: "",
  totalPages: "",
};

// Helper function to get status class and label
export const getDriverStatusInfo = (status, t) => {
  if (!status) return { className: "status unknown", label: t ? t("status_unknown") : "Unknown" };

  const statusLower = status.toLowerCase();

  switch (statusLower) {
    case "active":
      return { className: "status active", label: t ? t("status_active") : "Active" };
    case "inactive":
      return { className: "status inactive", label: t ? t("status_inactive") : "Inactive" };
    case "suspended":
      return { className: "status suspended", label: t ? t("status_suspended") : "Suspended" };
    case "pending":
      return { className: "status pending", label: t ? t("status_pending") : "Pending" };
    case "inreview":
      return { className: "status inreview", label: t ? t("status_in_review") : "In Review" };
    default:
      return { className: "status unknown", label: status.charAt(0).toUpperCase() + status.slice(1) };
  }
};

// Helper function to get duty status class and label
export const getDutyStatusInfo = (dutyStatus, t) => {
  if (!dutyStatus) return { className: "status unknown", label: t ? t("status_unknown") : "Unknown" };

  const statusLower = dutyStatus.toLowerCase();

  switch (statusLower) {
    case "available":
      return { className: "status available", label: t ? t("duty_available") : "Available" };
    case "reserved":
      return { className: "status reserved", label: t ? t("duty_reserved") : "Reserved" };
    case "on_route":
      return { className: "status onroute", label: t ? t("on_route") : "On Route" };
    case "off_duty":
      return { className: "status offduty", label: t ? t("off_duty") : "Off Duty" };
    case "unavailable":
      return { className: "status unavailable", label: t ? t("unavailable") : "Unavailable" };
    default:
      return { className: "status unknown", label: dutyStatus.charAt(0).toUpperCase() + dutyStatus.slice(1) };
  }
};

const Drivers = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [show2, setShow2] = useState(false);
  const { slug } = useParams();
  const { user } = useSelector((state) => state.UserReducer);
  const role = user?.role === "super_admin" ? "admin" : user?.role;
  const isAdmin = role === "admin";

  // slug base fetch data
  const userRole =
    slug === "school-drivers"
      ? "school"
      : slug === "vendor-drivers"
        ? "vendor"
        : null;

  const [id, setId] = useState("");
  const [driverDetails, setDriverDetails] = useState({});
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search.trim(), 300);
  const [drivers, setDrivers] = useState([]);
  const [action, setAction] = useState(initialActions);
  const [pagination, setPagination] = useState(initialPagination);

  const { data, isSuccess, isLoading, isError, error, isFetching, refetch } =
    useGetDriversQuery({
      page: pagination.page,
      limit: pagination.limit,
      ...action,
      role: userRole,
    });

  // delete query
  const [deleteDriver, { isLoading: isDeleteLoading }] =
    useDeleteDriverMutation();

  if (isError) {
    toast.dismiss();
    toast.error(getErrorMessage(error));
  }

  useEffect(() => {
    if (isSuccess && data?.data) {
      setDrivers(data?.data?.drivers);
      setPagination(data.data.pagination);
    }
  }, [data, isSuccess]);

  const handleShow2 = (data) => {
    setShow2(true);
    setDriverDetails(data);
  };

  // delete function
  const handleDelte = async () => {
    try {
      const response = await deleteDriver(id).unwrap();
      if (response.statusCode === 200) {
        toast.success(response.message);
        setShow(false);
        setId("");
      }
    } catch (err) {
      const mesg = err.data?.message || t("something_went_wrong");
      toast.error(mesg);
    }
  };

  // search State & Debounce Setup
  useEffect(() => {
    if (debouncedSearch) {
      // setPagination((prev) => ({ limit: 10, page: 1 }));
      setAction((prev) => ({ ...prev, search: search.trim() }));
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (!search.trim()) {
      // setPagination((prev) => ({ limit: 10, page: 1 }));
      setAction((prev) => ({ ...prev, search: "" }));
    }
  }, [search]);

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handleLimitChange = (limit) => {
    setPagination((prev) => ({
      ...prev,
      limit,
      page: 1,
    }));
  };

  const handleResetFilters = () => {
    setAction(initialActions);
    setPagination((prev) => ({
      ...prev,
      limit: 10,
      page: 1,
    }));
    setSearch("");
  };

  // Language Change Handler
  useEffect(() => {
    const h = () => {
      refetch?.();
    };
    i18n.on("languageChanged", h);
    return () => i18n.off("languageChanged", h);
  }, [refetch]);

  return (
    <>
      <div className="d-flex justify-content-between align-items-center flex-wrap w-100 mb-2">
        <div className="total">
          {t("total")} {t("drivers")}: {pagination?.total}
        </div>

        <div className="d-flex flex-wrap gap-2 ms-auto">
          <div className="d-flex flex-grow-1">
            <input
              type="text"
              className="form-control custom-field-one"
              placeholder={`${t("search")}..`}
              value={search}
              onChange={(e) => setSearch(e.target.value.trim())}
            />
          </div>

          <div className="d-flex gap-2 ms-auto">
            <button
              className="btn btn-primary addDriver"
              onClick={handleResetFilters}
            >
              {t("btns.reset")}
            </button>

            {!isAdmin && (
              <NavLink
                to="add"
                className="page-add-btn d-flex align-items-center justify-content-center gap-2"
              >
                <FaPlus /> {t("add")} {t("driver")}
              </NavLink>
            )}
          </div>
        </div>
      </div>

      <div className="table m-0">
        <table>
          <thead>
            <tr>
              <th className="text-nowrap">{`${t("Sr")}. ${t("no")}.`}</th>
              <th className="text-nowrap">{`${t("driver")} ${t("name")}`}</th>
              <th>{t("email")}</th>
              <th>{t("phone")}</th>
              <th className="text-nowrap">{`${t("license")} ${t("number")}`}</th>
              <th>{t("status")}</th>
              <th className="text-nowrap">{`${t("duty")} ${t("status")}`}</th>
              <th className="text-nowrap">
                {t("avg")} {t("rating")}
              </th>
              <th>{t("action")}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading || isFetching ? (
              <SkeletonLoading length={9} />
            ) : drivers?.length > 0 ? (
              drivers?.map((item, i) => {
                const link = isAdmin
                  ? `/${slug}/${item?.driver_details?.driver_id}/schedules`
                  : `/drivers/${item?.driver_details?.driver_id}/schedules`;

                const driverStatus = getDriverStatusInfo(item?.status, t);
                const dutyStatus = getDutyStatusInfo(
                  item?.driver_details?.duty_status,
                  t,
                );

                const isDriver = !!item?.driver_details?.driver_id;

                return (
                  <tr key={i}>
                    <td> {(pagination.page - 1) * pagination.limit + i + 1}</td>
                    <td className="text-nowrap">
                      <Tooltip
                        title={`${t("view")} ${item?.full_name || ""}'s ${t("schedule")} ${t("details")}`}
                      >
                        <NavLink
                          to={isDriver ? link : ""}
                          className="text-decoration-none text-primary fw-medium"
                        >
                          {item?.full_name || "N/A"}
                        </NavLink>
                      </Tooltip>
                    </td>
                    <td className="text-nowrap">{item?.email}</td>
                    <td className="text-nowrap">{item?.phone || "N/A"}</td>
                    <td className="text-nowrap">
                      {item?.driver_details?.license_number || "N/A"}
                    </td>
                    <td>
                      <span className={driverStatus.className}>
                        {driverStatus.label}
                      </span>
                    </td>
                    <td>
                      {dutyStatus.label !== "Unknown" ? (
                        <span className={dutyStatus.className}>
                          {dutyStatus.label}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td className="text-nowrap">
                      {item?.avg_rating != null ? (
                        <span className="ratingBadge">
                          ⭐ {item.avg_rating}
                          <span className="ratingCount">
                            {" "}
                            ({item.total_ratings})
                          </span>
                        </span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      <ActionMenu
                        actions={[
                          {
                            label: `${t("driver")} ${t("info")}`,
                            icon: <IoEye size={15} />,
                            onClick: () => handleShow2(item),
                          },
                          {
                            label: `${t("view")} ${t("schedule")}`,
                            icon: <IoEye size={15} />,
                            onClick: () => navigate(link),
                            hidden: !isDriver,
                          },
                        ]}
                      />
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr className="text-center">
                <td colSpan="9">
                  <div>
                    <span>{`${t("no")} ${t("driver")} ${t("found")}`}</span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <AdminPagination
        totalPages={pagination.totalPages}
        currentPage={pagination.page}
        limit={pagination.limit}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
      />

      {/* Delete Confirmation Modal */}
      <Modal show={show}>
        <Button className="closePopup" onClick={() => setShow(false)}>
          <IoCloseSharp />
        </Button>
        <div className="popupBox p-3 p-md-5">
          <div>
            <div className="text-center mb-3 errorIcon">
              <BiSolidError />
            </div>
            <div className="text-center mb-4 deleteHd">
              {t("confirmDelete")}
            </div>
            <div className="text-center">
              <Button
                className="smallBtn cancel me-3"
                onClick={() => setShow(false)}
              >
                {t("btns.cancel")}
              </Button>
              <Button className="smallBtn auto" onClick={handleDelte}>
                {isDeleteLoading ? (
                  <div className="spinner-border" role="status">
                    <span className="sr-only p-0"></span>
                  </div>
                ) : (
                  t("btns.delete")
                )}
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Driver Info Modal */}
      <Modal
        className="driverInfoPopup"
        show={show2}
        onHide={() => setShow2(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{`${t("driver")} ${t("info")}`}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <DriverInfo
            driverDetails={driverDetails}
            onHide={() => setShow2(false)}
            isAdmin={isAdmin}
          />
        </Modal.Body>
      </Modal>

      <style jsx>{`
        .status {
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 500;
          display: inline-block;
          border: 1px solid;
        }

        /* Driver Status Colors */
        .status.active {
          background-color: #d1fae5;
          color: #065f46;
          border-color: #a7f3d0;
        }

        .status.inactive {
          background-color: #f3f4f6;
          color: #4b5563;
          border-color: #d1d5db;
        }

        .status.suspended {
          background-color: #fee2e2;
          color: #991b1b;
          border-color: #fecaca;
        }

        .status.pending {
          background-color: #fef3c7;
          color: #92400e;
          border-color: #fde68a;
        }

        .status.inreview {
          background-color: #dbeafe;
          color: #1e40af;
          border-color: #bfdbfe;
        }

        .status.unknown {
          background-color: #e2e3e5;
          color: #383d41;
          border-color: #d6d8db;
        }

        /* Duty Status Colors */
        .status.available {
          background-color: #d1fae5;
          color: #065f46;
          border-color: #a7f3d0;
        }

        .status.reserved {
          background-color: #e0e7ff;
          color: #3730a3;
          border-color: #c7d2fe;
        }

        .status.onroute {
          background-color: #dbeafe;
          color: #1e40af;
          border-color: #bfdbfe;
        }

        .status.offduty {
          background-color: #f3f4f6;
          color: #4b5563;
          border-color: #d1d5db;
        }

        .status.unavailable {
          background-color: #fee2e2;
          color: #991b1b;
          border-color: #fecaca;
        }

        .actionBtn .iconBtn {
          padding: 6px;
          width: 36px;
          height: 36px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .actionBtn a {
          text-decoration: none;
        }

        .actionBtn a button {
          background: #f8f9fa;
          border: 1px solid #dee2e6;
          color: #495057;
          transition: all 0.2s;
        }

        .actionBtn a button:hover {
          background: #e9ecef;
          border-color: #adb5bd;
        }

        .ratingBadge {
          font-size: 13px;
          font-weight: 600;
          color: #92400e;
        }

        .ratingCount {
          font-size: 11px;
          font-weight: 400;
          color: #9ca3af;
        }
      `}</style>
    </>
  );
};
export default Drivers;
